import React, { useState, useEffect } from "react";
import api, { formatApiError } from "@/lib/api";
import { useFacility } from "@/context/FacilityContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { name: "", gender: "Male", age: "", dob: "", mobile: "", email: "", blood_group: "",
  address: "", emergency_contact_name: "", emergency_contact_phone: "", allergies: "", referred_by: "" };

function F({ label, children, full }) {
  return <div className={`space-y-1.5 ${full ? "col-span-2" : ""}`}><Label className="text-xs">{label}</Label>{children}</div>;
}

export function PatientDialog({ open, onOpenChange, initial, onSaved }) {
  const { activeId } = useFacility();
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [doctors, setDoctors] = useState([]);
  useEffect(() => { if (open) setForm({ ...EMPTY, ...(initial || {}) }); }, [open, initial]);
  useEffect(() => {
    if (open) api.get("/opd/meta").then((r) => setDoctors(r.data.doctors || [])).catch(() => setDoctors([]));
  }, [open]);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form, facility_id: activeId, age: form.age ? parseInt(form.age) : null };
      const { data } = initial?.id
        ? await api.put(`/opd/patients/${initial.id}`, payload)
        : await api.post("/opd/patients", payload);
      toast.success(initial?.id ? "Patient updated" : `Patient registered (${data.uhid})`);
      onOpenChange(false);
      onSaved && onSaved(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto thin-scroll" data-testid="patient-dialog">
        <DialogHeader>
          <DialogTitle>{initial?.id ? "Edit Patient" : "Register New Patient"}</DialogTitle>
          <DialogDescription>Enter the patient's details. Name is required; other fields are optional.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <F label="Full name" full><Input value={form.name} onChange={set("name")} data-testid="patient-name-input" /></F>
          <F label="Gender">
            <select className="w-full h-9 rounded-md border border-slate-300 px-3 text-sm bg-white" value={form.gender} onChange={set("gender")} data-testid="patient-gender-select">
              <option>Male</option><option>Female</option><option>Other</option>
            </select>
          </F>
          <F label="Age"><Input type="number" value={form.age ?? ""} onChange={set("age")} data-testid="patient-age-input" /></F>
          <F label="Date of birth"><Input type="date" value={form.dob || ""} onChange={set("dob")} /></F>
          <F label="Blood group"><Input value={form.blood_group || ""} onChange={set("blood_group")} placeholder="e.g. B+" /></F>
          <F label="Mobile"><Input value={form.mobile || ""} onChange={set("mobile")} data-testid="patient-mobile-input" /></F>
          <F label="Email"><Input value={form.email || ""} onChange={set("email")} /></F>
          <F label="Address" full><Textarea value={form.address || ""} onChange={set("address")} /></F>
          <F label="Emergency contact"><Input value={form.emergency_contact_name || ""} onChange={set("emergency_contact_name")} /></F>
          <F label="Emergency phone"><Input value={form.emergency_contact_phone || ""} onChange={set("emergency_contact_phone")} /></F>
          <F label="Known allergies" full><Input value={form.allergies || ""} onChange={set("allergies")} placeholder="e.g. Penicillin" /></F>
          <F label="Referred by" full>
            <Input list="referred-by-doctors" value={form.referred_by || ""} onChange={set("referred_by")}
              placeholder="Pick a doctor or type an external referrer's name" data-testid="patient-referred-by-input" />
            <datalist id="referred-by-doctors">
              {doctors.map((d) => <option key={d.id} value={d.name}>{d.designation || ""}</option>)}
            </datalist>
          </F>
          <div className="col-span-2 border-t border-slate-100 pt-3 mt-1"><div className="text-[11px] font-mono uppercase tracking-wider text-slate-400">Patient portal access (optional)</div></div>
          <F label="Portal email"><Input type="email" value={form.portal_email || ""} onChange={set("portal_email")} data-testid="patient-portal-email" placeholder="for patient login" /></F>
          <F label="Portal password"><Input type="text" value={form.portal_password || ""} onChange={set("portal_password")} data-testid="patient-portal-password" placeholder={initial?.id ? "leave blank to keep" : "set a password"} /></F>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={saving || !form.name} className="bg-blue-700 hover:bg-blue-800" data-testid="patient-save-button">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save patient"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
